import {
  FormFieldLookupTypeDTO,
  FormFieldResponseDTO,
  FormFieldTypeDTO,
  FormFieldValueDTO,
} from '@/api/base/DTOs';
import { config } from '@/forms/config';

import { FormPrimitiveValues, IFormInput } from '@/forms/typings';

const getFormFieldValueFromType = (
  value: FormFieldValueDTO,
  fieldType: FormFieldTypeDTO,
): FormPrimitiveValues => {
  switch (fieldType) {
    case FormFieldTypeDTO.Date: {
      const date = value.valueDate
        ? new Date(value.valueDate).toISOString().slice(0, 10)
        : '';

      return date;
    }
    case FormFieldTypeDTO.Integer:
    case FormFieldTypeDTO.Decimal:
      return value.valueNumber ?? 0;
    case FormFieldTypeDTO.Boolean:
      return value.valueBool ?? false;
    default:
      // Text fields normally carry a string value, but G1 can deliver numeric answers
      // (e.g. currency limits) in valueNumber — fall back to it so the value still renders.
      return value.value ?? (value.valueNumber != null ? String(value.valueNumber) : '');
  }
};

const getFormFieldValue = (
  value: FormFieldValueDTO,
  fieldType: FormFieldTypeDTO,
  lookupType?: FormFieldLookupTypeDTO,
  lookupTypeID?: number,
): FormPrimitiveValues => {
  switch (lookupType) {
    case FormFieldLookupTypeDTO.External:
      return value.value ?? '';
    case FormFieldLookupTypeDTO.Internal:
      return value.lookupID
        ? `${config.internalLookupPrefix}${lookupTypeID}${config.internalLookupPrefix}${value.lookupID}`
        : value.value ?? '';
    default:
      return getFormFieldValueFromType(value, fieldType);
  }
};

export const adaptFormFieldResponseToValue = (
  formFieldResponse: FormFieldResponseDTO,
): IFormInput['value'] => {
  const isMultiple =
    formFieldResponse.restriction?.allowMultipleValues ?? false;

  if (!formFieldResponse.values || formFieldResponse.values.length === 0) {
    return undefined;
  }

  if (isMultiple) {
    const values = formFieldResponse.values
      .map(v => {
        return getFormFieldValue(
          v,
          formFieldResponse.fieldType,
          formFieldResponse.lookupType,
          formFieldResponse.lookupType === FormFieldLookupTypeDTO.Internal
            ? formFieldResponse.internalLookup?.lookupTypeID
            : undefined,
        );
      })
      .join(config.multiValueDivider) as FormPrimitiveValues;

    return values;
  } else {
    return getFormFieldValue(
      formFieldResponse.values[0],
      formFieldResponse.fieldType,
      formFieldResponse.lookupType,
      formFieldResponse.lookupType === FormFieldLookupTypeDTO.Internal
        ? formFieldResponse.internalLookup?.lookupTypeID
        : undefined,
    );
  }
};
